<script lang="ts">
export const elDropdownMaxHeight = [0, 260, 1000] as const;
export type ElDropdownMaxHeight = (typeof elDropdownMaxHeight)[number];
</script>

<script setup lang="ts">
import CustomTransition from '@/_CustomTransition.vue';
import ElClipToAnchor from '@/ElClipToAnchor.vue';
import ElItem from '@/ElItem.vue';
import ElButton from '@/ElButton.vue';
import { ref, computed, watch } from 'vue';
import { onKeyStroke, onClickOutside } from '@vueuse/core';
import { omit } from 'lodash';
import ElIconButton from './ElIconButton.vue';

export type DropdownItem = { onClick?: () => void } & InstanceType<typeof ElItem>['$props'];

const props = withDefaults(
  defineProps<{
    items: DropdownItem[];
    title?: string;
    action?: Omit<InstanceType<typeof ElButton>['$props'], 'variant' | 'size'>;
    left?: boolean;
    right?: boolean;
    minWidth?: number;
    isOpen?: boolean;
    maxHeight?: ElDropdownMaxHeight;
    iconButton?: InstanceType<typeof ElIconButton>['$props'];
    multiple?: boolean;
  }>(),
  {
    title: undefined,
    action: undefined,
    isOpen: undefined,
    minWidth: undefined,
    maxHeight: 260,
    iconButton: undefined,
    multiple: false,
  },
);

const emit = defineEmits<{
  (event: 'click:action'): void;
  (event: 'update:is-open', isOpen: boolean): void;
}>();

const internalIsOpenState = ref<boolean>(false);
const listAnchor = ref<HTMLElement>();
const elClipToAnchor = ref<HTMLElement>();
const focusedIndex = ref<number>(0);
const itemRefs = ref<InstanceType<typeof ElItem>[]>();

const isOpenProxy = computed({
  set(isOpen: boolean) {
    if (props.isOpen !== undefined) {
      emit('update:is-open', isOpen);
    }
    internalIsOpenState.value = isOpen;
  },
  get(): boolean {
    if (props.isOpen !== undefined) return props.isOpen;
    return internalIsOpenState.value;
  },
});

const toggle = () => (isOpenProxy.value = !isOpenProxy.value);
const show = () => (isOpenProxy.value = true);
const hide = () => (isOpenProxy.value = false);

// Used to focus the first item when the dropdown is opened, so we can continue keyboard navigation from there
watch(
  () => [props.items, isOpenProxy.value],
  () => {
    if (isOpenProxy.value) {
      const selectedValueIdx = props.items?.findIndex(item => item.check);
      focusedIndex.value = selectedValueIdx || 0;
    }
  },
);

// Once focused item changes, we try to scroll the focused item into view if it's not already visible
const scrollToFocusedIndex = (focusedIndex: number) => {
  itemRefs.value?.at(focusedIndex)?.$el?.scrollIntoView({ block: 'nearest' });
};

const moveVisibleFocusDown = () => {
  focusedIndex.value += (itemRefs.value?.slice(focusedIndex.value + 1).findIndex(item => !item.$props.disabled) ?? -1) + 1;
  scrollToFocusedIndex(focusedIndex.value);
};

const moveVisibleFocusUp = () => {
  focusedIndex.value -=
    (itemRefs.value
      ?.slice(0, focusedIndex.value)
      .reverse()
      .findIndex(item => !item.$props.disabled) ?? -1) + 1;

  scrollToFocusedIndex(focusedIndex.value);
};

// Keyboard navigation handlers
onKeyStroke(['ArrowDown', 'ArrowUp', 'Enter'], e => {
  if (isOpenProxy.value === true) {
    switch (e.key) {
      case 'ArrowDown': {
        moveVisibleFocusDown();
        break;
      }
      case 'ArrowUp': {
        moveVisibleFocusUp();
        break;
      }
      case 'Enter': {
        const item = props.items[focusedIndex.value];

        if (!item.disabled) {
          item.onClick?.();
          hide();
        }

        break;
      }
    }

    e.preventDefault();
    e.stopPropagation();
  }
});

defineExpose({
  show,
  hide,
  toggle,
});

onClickOutside(props.multiple ? elClipToAnchor : listAnchor, event => {
  // Check if the clicked element is a disabled item
  const clickedElement = event.target as HTMLElement;
  const isDisabledItem = clickedElement.closest('[data-disabled="true"]');

  if (!isDisabledItem) {
    hide();
  }
});
</script>

<template>
  <div class="relative">
    <div ref="listAnchor">
      <slot
        name="anchor"
        v-bind="{
          isOpen: isOpenProxy,
          toggle,
          show,
          hide,
        }"
      >
        <ElIconButton v-if="iconButton" class="cursor-pointer" :aria-expanded="isOpenProxy" v-bind="iconButton" @click="toggle" />
      </slot>
    </div>

    <CustomTransition name="expand">
      <ElClipToAnchor
        v-if="isOpenProxy && listAnchor"
        ref="elClipToAnchor"
        class="pt-1"
        match-anchor-width
        :anchor="listAnchor"
        :position="left ? 'bottom-left' : right ? 'bottom-right' : 'bottom'"
        :min-width="minWidth ?? 208"
      >
        <div
          class="border-neutral-surface text-neutral-darker inline-flex w-full flex-col items-start gap-1 rounded-md border bg-white p-2 text-sm shadow-md max-h-[inherit]"
        >
          <div v-if="title" class="border-neutral-surface flex h-9 items-center self-stretch border-b px-2">
            <span class="font-semibold">{{ title }}</span>
          </div>
          <div
            class="no-scrollbar relative w-full overflow-y-scroll"
            :class="{
              'max-h-[260px]': maxHeight === 260,
            }"
            role="listbox"
            :aria-activedescendant="'option-' + focusedIndex"
          >
            <div class="flex w-full flex-col gap-1">
              <template v-if="items.length > 0">
                <ElItem
                  v-for="(item, index) in items"
                  :id="'option-' + index"
                  ref="itemRefs"
                  :key="index"
                  class="self-stretch"
                  :class="{
                    'cursor-pointer': !item.disabled,
                    'cursor-default': item.disabled,
                  }"
                  role="option"
                  tabindex="-1"
                  v-bind="omit(item, 'onClick')"
                  :disabled="item.disabled"
                  :data-disabled="item.disabled ? 'true' : undefined"
                  :data-cy="`${item.text?.trim().replaceAll(' ', '').replaceAll(',', '')}-dropdown-item`"
                  :focused="focusedIndex === index"
                  @click.prevent="!item.disabled && item.onClick?.()"
                />
              </template>
              <ElItem v-else class="cursor-pointer self-stretch" :data-cy="`no-items-dropdown-item`" text="Nessuna opzione disponibile" />
            </div>
          </div>
          <div v-if="action" class="border-neutral-surface self-stretch border-t py-2 px-5">
            <ElButton variant="tertiary" v-bind="action" class="w-full" @click="emit('click:action')" />
          </div>
        </div>
      </ElClipToAnchor>
    </CustomTransition>
  </div>
</template>
